import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ChevronLeft, ChevronRight, Clock, GraduationCap, Target, BookOpen, ShieldCheck, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COURS_SESSION_CIRCULATION } from '../data/coursSessionCirculation';
import useSeo from '../hooks/useSeo';

export default function CoursSessionCirculation() {
  const navigate = useNavigate();
  const published = useMemo(
    () => COURS_SESSION_CIRCULATION.filter((c) => c.status === 'published'),
    []
  );

  useSeo({
    title: 'Formations spécifiques 7h moto — Passerelle A2→A · 125 · BSR · Espace Pro',
    description:
      "Programmes COMPLETS des 3 formations obligatoires hors REMC moto : Passerelle A2→A (7h, arrêté 01/06/2016), 7h Permis B→125 cc/L5e (arrêté 07/01/2019), BSR/Permis AM 8h (arrêté 08/11/2012). Issus de notes terrain ECSR.",
    path: '/enseignant/cours-circulation',
  });

  return (
    <div className="space-y-6" data-testid="cours-session-circulation-page">
      {/* Breadcrumb */}
      <nav aria-label="Fil d'Ariane">
        <button
          onClick={() => navigate('/enseignant')}
          className="inline-flex items-center gap-1 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="breadcrumb-back-teacher"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Espace Pro ECSR
        </button>
      </nav>

      {/* Hero */}
      <section className="mc-card relative overflow-hidden" data-testid="cours-circulation-hero">
        <div
          className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl opacity-30"
          style={{ background: '#cf2031' }}
        />
        <div className="relative p-6 sm:p-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> Formations spécifiques · Hors REMC
          </div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.05]">
            <span className="text-[#ff7a84]">Formations spécifiques</span> 7h moto.
          </h1>
          <p className="text-sm sm:text-lg text-foreground/80 mt-4 max-w-3xl leading-relaxed">
            Les <strong>3 formations obligatoires hors REMC</strong> avec leur propre cadre légal :
            Passerelle A2→A après 2 ans, 7h B→125 cc/L5e, et BSR/Permis AM dès 14 ans.
            Chaque programme intègre objectifs, déroulé minuté, supports élève et références juridiques.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-5 text-xs font-bold">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1.5">
              <Target className="w-3.5 h-3.5" /> Compétences REMC
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1.5">
              <Clock className="w-3.5 h-3.5" /> Déroulé minuté
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1.5">
              <FileText className="w-3.5 h-3.5" /> Évaluation finale
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1.5">
              <BookOpen className="w-3.5 h-3.5" /> Références juridiques
            </span>
          </div>
        </div>
      </section>

      {/* Liste des fiches */}
      <section className="space-y-3" data-testid="cours-circulation-list">
        <h2 className="text-xl sm:text-2xl font-black text-foreground flex items-center gap-2">
          <GraduationCap className="w-6 h-6 text-[hsl(var(--primary))]" />
          Formations publiées
          <span className="text-xs font-bold text-muted-foreground">· {published.length} programmes</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {published.map((c) => {
            const Icon = Icons[c.icon] || Icons.BookOpen;
            return (
              <Link
                key={c.slug}
                to={`/enseignant/cours-circulation/${c.slug}`}
                className="mc-card p-5 sm:p-6 group hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-200 block"
                data-testid={`cours-card-${c.slug}`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 text-white"
                    style={{ background: c.accent }}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                      <span>Fiche n°{c.number}</span>
                      <span>·</span>
                      <span>{c.level}</span>
                      <span>·</span>
                      <span>{c.duration}</span>
                    </div>
                    <h3 className="font-black text-lg sm:text-xl leading-tight mt-1.5 text-foreground group-hover:text-[hsl(var(--primary))] transition-colors">
                      {c.title}
                    </h3>
                    <p className="text-sm text-foreground/75 mt-2 leading-relaxed">{c.teaser}</p>
                    <div className="mt-3 flex items-center gap-2 text-xs font-bold text-[hsl(var(--primary))]">
                      <span>Ouvrir la fiche séance</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* À venir */}
        <div className="mc-card p-5 sm:p-6 mt-6 border-dashed" data-testid="cours-coming-soon">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">
            Prochaines fiches en préparation
          </div>
          <p className="text-sm text-foreground/75 leading-relaxed">
            D'autres fiches séances type jury seront ajoutées au fil des semaines (regards et trajectoires en
            intersection, partage de la route avec usagers vulnérables, conduite par tous temps…).
            Envoie de nouvelles captures pédagogiques pour alimenter la collection.
          </p>
        </div>
      </section>
    </div>
  );
}
